"use client";

import type { ComponentType } from "react";
import dynamic from "next/dynamic";
import { useEffect, useState } from "react";
import { KineticHeadline } from "@/components/ui/kinetic-headline";
import { HeroIllustration } from "@/components/ui/hero-illustration";
import { Container } from "@/components/layout/container";
import { canRenderHeavyWebgl } from "@/lib/webgl-capability";

const PageHeroCanvas = dynamic(
  () => import("@/components/three/page-hero-canvas").then((m) => m.PageHeroCanvas),
  { ssr: false }
);

type PageHeroProps = {
  id: string;
  eyebrow: string;
  lines: string[];
  description: string;
  accentColor?: string;
  seed?: number;
  /** Optional right-column illustration (an inline undraw.co component from
   * components/illustrations/). Pages that don't pass this keep the original single-column layout
   * — e.g. Privacy Policy stays deliberately plain/low-motion. */
  illustration?: ComponentType<React.SVGProps<SVGSVGElement>>;
  illustrationLabel?: string;
  /** Passed straight through to KineticHeadline. Defaults to "slide-up" — pages that want the
   * page-wide continuous word-flip effect (see kinetic-headline.tsx) pass "word-flip" explicitly;
   * deliberately plain/low-motion pages (Privacy Policy) should leave this unset. */
  headlineVariant?: "slide-up" | "blur-focus" | "word-flip";
  canvas?: boolean;
};

/** Compact, 3D-animated page hero used by every non-home marketing page (Industries, Services,
 * Portfolio) — same live Three.js/WebGL requirement as the homepage, scaled down to a single
 * kinetic headline + a code-generated particle/orb field rather than a full chapter sequence. */
export function PageHero({
  id,
  eyebrow,
  lines,
  description,
  accentColor = "#a374e8",
  seed = 7,
  illustration,
  illustrationLabel,
  headlineVariant,
  canvas,
}: PageHeroProps) {
  const [canRender3D, setCanRender3D] = useState(false);
  // Canvas defaults off when an illustration carries the visual; explicit `canvas` overrides.
  const canvasEnabled = canvas ?? !illustration;

  useEffect(() => {
    if (!canvasEnabled) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    // Skip the WebGL particle hero on reduced-motion and on software/no-GPU renderers (where it
    // would rasterize on the CPU and block the main thread). Same capability gate as the homepage.
    if (reduce || !canRenderHeavyWebgl()) return;

    // Defer the (large, ~230 KB) Three.js chunk's download + parse + mount past hydration instead
    // of eagerly importing it at module scope — a hero is above the fold but its 3D backdrop is
    // pure decoration, so it must not sit on the LCP/hydration critical path of every sub-page.
    let mountId: number | null = null;
    const hasIdle = typeof window.requestIdleCallback === "function";
    const prefetch = () => {
      import("@/components/three/page-hero-canvas").then(() => {
        mountId = hasIdle
          ? window.requestIdleCallback(() => setCanRender3D(true), { timeout: 1500 })
          : window.setTimeout(() => setCanRender3D(true), 200);
      });
    };
    const prefetchId = hasIdle
      ? window.requestIdleCallback(prefetch, { timeout: 2500 })
      : window.setTimeout(prefetch, 300);

    return () => {
      if (hasIdle) {
        window.cancelIdleCallback(prefetchId as number);
        if (mountId !== null) window.cancelIdleCallback(mountId);
      } else {
        window.clearTimeout(prefetchId as number);
        if (mountId !== null) window.clearTimeout(mountId);
      }
    };
  }, [canvasEnabled]);

  return (
    <section
      id={id}
      aria-labelledby={`${id}-heading`}
      className="relative flex items-start overflow-hidden pt-24 sm:pt-32 pb-10 sm:pb-16 lg:min-h-[60vh]"
    >
      {canRender3D ? (
        // No LazyCanvasMount here — see the module-load comment above. This hero is the only
        // canvas on its page, so mounting it directly (never unmounting on scroll) is correct.
        <div className="absolute inset-0" aria-hidden>
          <PageHeroCanvas color={accentColor} seed={seed} />
        </div>
      ) : (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 -z-10"
          style={{
            background: `radial-gradient(70% 60% at 25% 30%, color-mix(in oklch, ${accentColor} 20%, transparent) 0%, transparent 65%)`,
          }}
        />
      )}

      <Container
        className={`relative z-10 ${
          illustration ? "grid items-start gap-10 lg:grid-cols-2" : ""
        }`}
      >
        <div className={illustration ? "max-w-xl" : undefined}>
          <span className="font-mono-tech inline-flex w-fit items-center gap-2 rounded-full border border-[var(--color-border)] bg-[var(--color-bg)]/60 px-3 py-1.5 text-[0.7rem] uppercase tracking-[0.15em] text-[var(--color-primary-text)] backdrop-blur-sm">
            <span className="h-1.5 w-1.5 rounded-full bg-[var(--color-primary-light)]" />
            {eyebrow}
          </span>
          <KineticHeadline
            as="h1"
            id={`${id}-heading`}
            lines={lines}
            variant={headlineVariant}
            className="mt-6 font-display text-[clamp(2.25rem,4.5vw,4rem)] font-bold leading-[1.02] tracking-[-0.02em] text-[var(--color-text-primary)]"
          />
          <p className="mt-6 max-w-xl text-lg leading-relaxed text-[var(--color-text-secondary)]">
            {description}
          </p>
        </div>

        {illustration && (
          <HeroIllustration illustration={illustration} label={illustrationLabel ?? ""} />
        )}
      </Container>
    </section>
  );
}
